import { FormControl, Select, MenuItem, Typography } from "@mui/material";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { useTranslation } from "../../../hooks/useTranslation";
import { useFiltersWithContext } from "../filter-context/useFiltersWithContext";
import React from "react";

type DropdownFilterProps = {
  name: string;
  labelKey: string;
  options: { value: string | boolean; label: string }[];
  operator?: "eq" | "contains";
  hideLabelInChip?: boolean;
  renderOption?: (_option: {
    value: string | boolean;
    label: string;
    [key: string]: unknown;
  }) => React.ReactNode;
};

export const DropdownFilter = (props: DropdownFilterProps) => {
  const {
    name,
    labelKey,
    options,
    operator = "contains",
    hideLabelInChip,
  } = props;
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);

  const { getFilterValue, setFilter, deleteFilter } = useFiltersWithContext();
  const { translate } = useTranslation();

  const handleSelect = (filterName: string, value: string) => {
    const optionLabel =
      options.find((option) => option.value === value)?.label ??
      "filters.placeholders.none";

    setFilter({
      field: filterName,
      value: value,
      labelInfo: {
        descriptionKey: hideLabelInChip ? optionLabel : labelKey,
        value: hideLabelInChip ? undefined : optionLabel,
        isValueTranslated: true,
      },
      operator: operator,
    });
  };

  const handleClear = (filterName: string) => {
    deleteFilter(filterName, operator);
  };

  return (
    <FormControl
      fullWidth
      sx={{
        display: "flex",
        flexDirection: "row",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Typography flex={1}>
        {translate(labelKey, { market: selectedCountry?.countryName })}
      </Typography>
      <Select
        size="small"
        sx={{ minWidth: 200 }}
        value={getFilterValue(name, operator) ?? "default"}
        onChange={(e) => {
          const value = e.target.value as string;
          return value != "default"
            ? handleSelect(name, value)
            : handleClear(name);
        }}
      >
        <MenuItem value="default">
          <em>{translate("filters.placeholders.none")}</em>
        </MenuItem>
        {options.map((option) => (
          <MenuItem key={`key-${option.value}`} value={option.value.toString()}>
            {props.renderOption
              ? props.renderOption(option)
              : translate(option.label, {
                  market: selectedCountry?.countryName,
                })}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
